import { FC, PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from '@/shared/components/toasts/PlatformToast';

import { authorizedAxiosInstance } from '@/clients';

import {
    getSearchCompanySyncSummary,
    isTerminalSearchCompanySyncStatus,
    QueuedSearchCompanySync,
    SearchCompanySyncProgressContext,
    SearchCompanySyncRequestProgress,
    SearchCompanySyncRun,
} from './searchCompanySyncProgressState';

const SearchCompanySyncProgressProvider: FC<PropsWithChildren> = ({ children }): JSX.Element => {
    const [runs, setRuns] = useState<SearchCompanySyncRun[]>([]);
    const runsRef = useRef(runs);
    const notifiedRuns = useRef(new Set<string>());
    const removalTimers = useRef(new Map<string, number>());

    useEffect(() => {
        runsRef.current = runs;
    }, [runs]);

    const trackQueuedSyncs = useCallback((syncs: QueuedSearchCompanySync[], companyCount: number) => {
        const requests = syncs.flatMap((sync) =>
            sync.statusUrl && sync.syncRequestId
                ? [
                      {
                          expectedTotal: Math.max(Number(sync.queued ?? 0), 0),
                          statusUrl: sync.statusUrl,
                          syncRequestId: sync.syncRequestId,
                      },
                  ]
                : [],
        );

        if (!requests.length || companyCount < 1) {
            return;
        }

        setRuns((currentRuns) => [
            ...currentRuns,
            {
                companyCount,
                id: requests
                    .map((request) => request.syncRequestId)
                    .sort()
                    .join(':'),
                requests,
            },
        ]);
    }, []);

    const activeRequestKey = runs
        .flatMap((run) =>
            run.requests
                .filter((request) => !isTerminalSearchCompanySyncStatus(request.progress?.status))
                .map((request) => request.syncRequestId),
        )
        .sort()
        .join(':');

    useEffect(() => {
        if (!activeRequestKey) {
            return;
        }

        let cancelled = false;
        let polling = false;

        const poll = async () => {
            if (polling) {
                return;
            }
            polling = true;

            const pendingRequests = runsRef.current.flatMap((run) =>
                run.requests.filter((request) => !isTerminalSearchCompanySyncStatus(request.progress?.status)),
            );
            const statuses = await Promise.allSettled(
                pendingRequests.map(async (request) => ({
                    progress: (await authorizedAxiosInstance.get(request.statusUrl))
                        .data as SearchCompanySyncRequestProgress,
                    syncRequestId: request.syncRequestId,
                })),
            );

            polling = false;
            if (cancelled) {
                return;
            }

            const progressByRequestId = new Map(
                statuses.flatMap((status) =>
                    status.status === 'fulfilled' ? [[status.value.syncRequestId, status.value.progress] as const] : [],
                ),
            );

            if (!progressByRequestId.size) {
                return;
            }

            setRuns((currentRuns) =>
                currentRuns.map((run) => ({
                    ...run,
                    requests: run.requests.map((request) => ({
                        ...request,
                        progress: progressByRequestId.get(request.syncRequestId) ?? request.progress,
                    })),
                })),
            );
        };

        void poll();
        const interval = window.setInterval(() => void poll(), 3_000);

        return () => {
            cancelled = true;
            window.clearInterval(interval);
        };
    }, [activeRequestKey]);

    useEffect(() => {
        runs.forEach((run) => {
            const completed =
                run.requests.length > 0 &&
                run.requests.every((request) => isTerminalSearchCompanySyncStatus(request.progress?.status));

            if (!completed || notifiedRuns.current.has(run.id)) {
                return;
            }

            notifiedRuns.current.add(run.id);
            const failedOperations = run.requests.reduce(
                (total, request) => total + (request.progress?.failed ?? 0),
                0,
            );
            const totalOperations = run.requests.reduce(
                (total, request) => total + (request.progress?.total ?? request.expectedTotal),
                0,
            );

            if (failedOperations > 0) {
                const successfulCompanyCount =
                    totalOperations > 0
                        ? Math.max(
                              Math.floor(run.companyCount * ((totalOperations - failedOperations) / totalOperations)),
                              0,
                          )
                        : 0;

                toast.warning('Sync completed with errors', {
                    description: `${successfulCompanyCount.toLocaleString()} of ${run.companyCount.toLocaleString()} companies synced successfully.`,
                });
            } else {
                toast.success('Sync completed', {
                    description: `${run.companyCount.toLocaleString()} companies synced successfully.`,
                });
            }

            const timer = window.setTimeout(() => {
                setRuns((currentRuns) => currentRuns.filter((currentRun) => currentRun.id !== run.id));
                removalTimers.current.delete(run.id);
            }, 2_500);
            removalTimers.current.set(run.id, timer);
        });
    }, [runs]);

    useEffect(
        () => () => {
            removalTimers.current.forEach((timer) => window.clearTimeout(timer));
        },
        [],
    );

    const value = useMemo(
        () => ({
            progress: getSearchCompanySyncSummary(runs),
            trackQueuedSyncs,
        }),
        [runs, trackQueuedSyncs],
    );

    return (
        <SearchCompanySyncProgressContext.Provider value={value}>{children}</SearchCompanySyncProgressContext.Provider>
    );
};

export default SearchCompanySyncProgressProvider;
